/**
 * Character Creator – Refactored for DreamMee MVP
 * Accordion-based UI, skin-tone sync, recipe I/O
 * Renders into an external canvas container + controls panel
 */

class CharacterCreator {
  constructor(canvasContainerId, controlsPanelId, config, options = {}) {
    this.canvasContainerId = canvasContainerId;
    this.controlsPanel = document.getElementById(controlsPanelId);
    this.config = config;
    this.skipPreload = !!options.skipPreload;
    this.allowedPoses = options.allowedPoses || null;

    const initialPose = this.getDefaultPose();

    // State
    this.state = {
      pose: initialPose,
      selections: { ...config.defaults },
      // #3: Multi-select categories store arrays of asset IDs
      multiSelections: {
        extras: config.defaults.extras ? [config.defaults.extras] : []
      },
      // Per-group gradient tone selections
      toneSelections: { skin: 'none', hair: 'none', eyes: 'none', top_clothing: 'none', bottom_clothing: 'none', shoes: 'none' },
      // Currently active outfit set ID (or null if none)
      activeOutfitSet: null
    };

    this.app = options.app || null;

    // If the initial pose is different from the global default pose, initialize selections for its specific categories
    if (initialPose !== config.defaults.pose) {
      const poseAssets = this.config.assets[initialPose] || {};
      for (const [category, assets] of Object.entries(poseAssets)) {
        if (!this.state.selections[category] && assets.length > 0) {
          this.state.selections[category] = assets[0].id;
        }
      }
    }

    // Konva
    this.stage = null;
    this.mainLayer = null;
    this.layerGroups = {};
    this.imageCache = new Map();
    this.maxCacheSize = (typeof DREAMMEE_DEPLOY !== 'undefined' && DREAMMEE_DEPLOY.maxImageCacheSize) || 80;
    this.loadCounters = {};

    // UI
    this.openAccordion = null; // id of currently open accordion
    this.activeSubSection = {}; // per accordion group

    this.init();
  }

  // ==================== INITIALIZATION ====================

  async init() {
    this.initKonva();

    if (this.skipPreload) {
      // Admin/headless mode: skip UI rendering and default-pose load.
      // The caller will set the recipe state and call loadCharacter() explicitly.
      this._preloadAborted = false;
      this._preloadPromise = null;
      return;
    }

    this.renderControls();
    this.bindToolbar();

    // Wait for the initial character to render on canvas before preloading.
    // This prevents the preload from competing with loadCharacter() and
    // pose icon <img> tags for iOS Safari's 6-connection-per-host limit.
    await this.loadCharacter();

    // Tiered preload: current pose first, then tilted variant, then rest during idle
    this._preloadAborted = false;
    this._preloadPromise = null;
    this._startPreloadWhenReady();
  }

  getDefaultPose() {
    const defaultPose = this.config.defaults.pose;
    if (this.allowedPoses && this.allowedPoses.length > 0) {
      const isAllowed = (poseId) => {
        if (this.allowedPoses.includes(poseId)) return true;
        const poseObj = this.config.poses.find(p => p.id === poseId);
        if (poseObj && poseObj.tiltedOf && this.allowedPoses.includes(poseObj.tiltedOf)) return true;
        return false;
      };
      if (!isAllowed(defaultPose)) {
        const firstAllowed = this.allowedPoses.find(id => this.config.poses.some(p => p.id === id));
        if (firstAllowed) return firstAllowed;
      }
    }
    return defaultPose;
  }

  /**
   * Get canvas dimensions for the current pose.
   * Uses per-pose canvasSize override if defined, otherwise falls back to global config.
   */
  getCanvasSize() {
    const pose = this.config.poses.find(p => p.id === this.state.pose);
    if (pose && pose.canvasSize) {
      return { width: pose.canvasSize.width, height: pose.canvasSize.height };
    }
    return { width: this.config.canvas.width, height: this.config.canvas.height };
  }

  initKonva() {
    const { width, height } = this.getCanvasSize();

    this.stage = new Konva.Stage({
      container: this.canvasContainerId,
      width,
      height
    });

    // Transparent background (scene composer will provide the actual bg)
    this.recreateLayers();
    this.handleResize();
    window.addEventListener('resize', () => this.handleResize());
  }

  recreateLayers() {
    if (this.mainLayer) {
      this.deallocateCanvases(this.mainLayer);
      this.mainLayer.destroy();
    }
    this.layerGroups = {};

    const pose = this.config.poses.find(p => p.id === this.state.pose);
    if (!pose) return;

    this.mainLayer = new Konva.Layer();
    this.stage.add(this.mainLayer);

    pose.zIndexConfig.forEach(cat => {
      const group = new Konva.Group();
      this.layerGroups[cat] = group;
      this.mainLayer.add(group);
    });
  }

  deallocateCanvases(node) {
    if (!node) return;
    node.find('Image').forEach(imgShape => {
      const img = imgShape.image();
      if (img && img instanceof HTMLCanvasElement) {
        img.width = 0;
        img.height = 0;
      }
    });
  }

  destroy() {
    if (this.mainLayer) {
      this.deallocateCanvases(this.mainLayer);
      this.mainLayer.destroy();
      this.mainLayer = null;
    }
    if (this.stage) {
      this.stage.destroy();
      this.stage = null;
    }
    this.layerGroups = {};
    this.imageCache.clear();
    const container = document.getElementById(this.canvasContainerId);
    if (container) {
      container.innerHTML = '';
    }
  }

  // Getter for backward-compatibility with admin-core.js direct layer cloning queries
  get layers() {
    console.warn("[CharacterCreator] get layers() is deprecated and will be removed. Please use layerGroups instead.");
    return this.layerGroups;
  }

  handleResize() {
    const container = document.getElementById(this.canvasContainerId);
    if (!container) return;
    const containerWidth = container.offsetWidth;
    if (containerWidth === 0) return;
    const { width, height } = this.getCanvasSize();
    const scale = containerWidth / width;
    this.stage.width(width * scale);
    this.stage.height(height * scale);
    this.stage.scale({ x: scale, y: scale });
  }

  bindToolbar() {
    const canvasArea = document.getElementById(this.canvasContainerId)?.closest('.canvas-area');
    if (canvasArea) {
      const randomBtn = canvasArea.querySelector('.toolbar-btn[title="Randomize"]') || canvasArea.querySelector('.toolbar-btn:first-child');
      const resetBtn = canvasArea.querySelector('.toolbar-btn[title="Reset"]') || canvasArea.querySelector('.toolbar-btn:last-child');
      if (randomBtn) randomBtn.addEventListener('click', () => this.randomize());
      if (resetBtn) resetBtn.addEventListener('click', () => this.reset());
    } else {
      const randomBtn = document.getElementById('cc-randomize') || document.getElementById('char-randomize');
      const resetBtn = document.getElementById('cc-reset') || document.getElementById('char-reset');
      if (randomBtn) randomBtn.addEventListener('click', () => this.randomize());
      if (resetBtn) resetBtn.addEventListener('click', () => this.reset());
    }
  }

  // ==================== PRELOAD (3-tier priority) ====================

  /**
   * Wait for pose icon <img> tags to finish loading, then start preload.
   * This ensures UI-critical images aren't starved by background fetches,
   * especially on iOS Safari which limits connections to 6 per hostname.
   */
  _startPreloadWhenReady() {
    const poseIcons = this.controlsPanel
      ? Array.from(this.controlsPanel.querySelectorAll('[data-pose] img'))
      : [];

    if (poseIcons.length === 0 || poseIcons.every(img => img.complete)) {
      this.preloadAllAssets();
      return;
    }

    // Wait for all pose icons, then start preload
    let remaining = poseIcons.filter(img => !img.complete).length;
    const onDone = () => {
      remaining--;
      if (remaining <= 0) this.preloadAllAssets();
    };
    poseIcons.forEach(img => {
      if (img.complete) return;
      img.addEventListener('load', onDone, { once: true });
      img.addEventListener('error', onDone, { once: true });
    });

    // Safety fallback: start anyway after 3s in case icons stall
    setTimeout(() => {
      if (!this._preloadPromise) this.preloadAllAssets();
    }, 3000);
  }

  /**
   * Start tiered preloading. Aborts any in-progress preload and restarts
   * with the current pose as Tier 1 priority.
   */
  async preloadAllAssets() {
    // Abort previous preload if still running
    if (this._preloadPromise) {
      this._preloadAborted = true;
      await this._preloadPromise;
    }
    this._preloadAborted = false;
    this._preloadPromise = this._runTieredPreload();
    return this._preloadPromise;
  }

  /**
   * Internal: execute 3-tier preload.
   * Tier 1 — current pose (remaining uncached assets, moderate concurrency)
   * Tier 2 — tilted variant of current pose (deferred)
   * Tier 3 — all remaining poses (idle-time, low concurrency)
   *
   * Concurrency is kept conservative (4/3/2) to avoid saturating iOS Safari's
   * 6-connection-per-hostname limit. On HTTP/2 (Shopify CDN) this still
   * allows good throughput via multiplexing.
   */
  async _runTieredPreload() {
    const currentPoseId = this.state.pose;

    // --- Tier 1: Current pose active selections ---
    const activeUrls = this._getActiveSelectionUrls(currentPoseId);
    const uncachedActive = activeUrls.filter(url => {
      const { width } = this.getCanvasSize();
      const resolved = resolveAssetUrl(url, { maxWidth: width * 2 });
      return !this.imageCache.has(resolved);
    });

    if (uncachedActive.length > 0) {
      console.log(`[Character] Tier 1: Preloading ${uncachedActive.length} active selections for "${currentPoseId}"...`);
      for (let i = 0; i < uncachedActive.length; i += 4) {
        if (this._preloadAborted) return;
        const chunk = uncachedActive.slice(i, i + 4);
        await Promise.allSettled(chunk.map(src => this.loadImage(src)));
        await new Promise(resolve => setTimeout(resolve, 16));
      }
    }
    if (this._preloadAborted) return;

    // --- Tier 2: Tilted companion active selections ---
    const currentPose = this.config.poses.find(p => p.id === currentPoseId);
    const tiltedId = currentPose?.tiltedOf
      ? currentPose.tiltedOf
      : this.config.poses.find(p => p.tiltedOf === currentPoseId)?.id;

    if (tiltedId) {
      const companionUrls = this._getActiveSelectionUrls(tiltedId);
      const uncachedCompanion = companionUrls.filter(url => {
        const { width } = this.getCanvasSize();
        const resolved = resolveAssetUrl(url, { maxWidth: width * 2 });
        return !this.imageCache.has(resolved);
      });

      if (uncachedCompanion.length > 0) {
        console.log(`[Character] Tier 2: Preloading ${uncachedCompanion.length} active selections for companion pose "${tiltedId}"...`);
        for (let i = 0; i < uncachedCompanion.length; i += 4) {
          if (this._preloadAborted) return;
          const chunk = uncachedCompanion.slice(i, i + 4);
          await Promise.allSettled(chunk.map(src => this.loadImage(src)));
          await new Promise(resolve => setTimeout(resolve, 16));
        }
      }
    }

    if (!this._preloadAborted) {
      console.log(`[Character] Preload complete (active selections). Cache size: ${this.imageCache.size}`);
    }
  }

  _getActiveSelectionUrls(poseId) {
    const urls = [];
    const categories = this.config.assets[poseId];
    if (!categories) return urls;

    for (const category of Object.keys(categories)) {
      // 1. Single selections
      const assetId = this.state.selections[category];
      if (assetId) {
        const asset = categories[category].find(a => a.id === assetId);
        if (asset?.src) urls.push(asset.src);
      }
      // 2. Multi selections (e.g. extras/accessories)
      const multiSelects = this.state.multiSelections[category] || [];
      for (const mId of multiSelects) {
        const asset = categories[category].find(a => a.id === mId);
        if (asset?.src) urls.push(asset.src);
      }
    }
    return urls;
  }

  // ==================== ACCORDION UI ====================

  /**
   * Get the active accordion sections for the current pose.
   * Uses POSE_ACCORDION_MAP overrides if available, otherwise falls back to config default.
   */
  getAccordionSections() {
    if (typeof POSE_ACCORDION_MAP !== 'undefined' && POSE_ACCORDION_MAP[this.state.pose]) {
      return POSE_ACCORDION_MAP[this.state.pose];
    }
    return this.config.accordionSections;
  }

  renderControls() {
    if (!this.controlsPanel) return;

    const sections = this.getAccordionSections();
    this.controlsPanel.innerHTML = sections.map((section, idx) => {
      const isOpen = this.openAccordion === section.id || (this.openAccordion === null && idx === 0);
      if (isOpen && this.openAccordion === null) this.openAccordion = section.id;

      return `
        <div class="accordion ${isOpen ? 'accordion--open' : ''}" data-accordion="${section.id}">
          <button class="accordion__header" data-accordion-toggle="${section.id}">
            <span class="accordion__icon">${section.icon}</span>
            <span class="accordion__title">${section.label}</span>
            <span class="accordion__chevron">▼</span>
          </button>
          <div class="accordion__body" id="acc-body-${section.id}">
            ${this.renderAccordionBody(section)}
          </div>
        </div>
      `;
    }).join('');

    // Bind event listeners (delegated)
    this.bindControlEvents();
  }

  renderAccordionBody(section) {
    if (section.type === 'pose') {
      return this.renderPoseGrid();
    }

    if (section.subSections) {
      // Filter sub-sections to only those that have assets for the current pose
      // OR are special types like outfit_set
      const availableSubs = section.subSections.filter(sub => {
        if (sub.type === 'outfit_set') {
          // Show outfit_set tab only if this pose has outfit set bundles
          return typeof OUTFIT_SET_BUNDLES !== 'undefined' && OUTFIT_SET_BUNDLES[this.state.pose];
        }
        const assets = this.config.assets[this.state.pose]?.[sub.id];
        return assets && assets.length > 0;
      });

      if (availableSubs.length === 0) return '<div style="padding:12px;color:var(--dm-text-muted);font-size:0.8rem;">No options available for this pose</div>';

      const activeSubId = this.activeSubSection[section.id] || availableSubs[0].id;
      // Reset active sub if it's no longer available
      if (!availableSubs.find(s => s.id === activeSubId)) {
        this.activeSubSection[section.id] = availableSubs[0].id;
      } else if (!this.activeSubSection[section.id]) {
        this.activeSubSection[section.id] = activeSubId;
      }
      const currentSubId = this.activeSubSection[section.id] || availableSubs[0].id;

      const subTabs = availableSubs.map(sub => `
        <button class="sub-tab ${currentSubId === sub.id ? 'sub-tab--active' : ''}"
                data-group="${section.id}" data-sub="${sub.id}">
          ${sub.label}
        </button>
      `).join('');

      // Render either outfit set grid or normal asset grid
      const currentSub = availableSubs.find(s => s.id === currentSubId);
      let grid;
      if (currentSub?.type === 'outfit_set') {
        grid = this.renderOutfitSetGrid();
      } else {
        grid = this.renderAssetGrid(currentSubId);
      }

      // Add inline tone picker for eligible groups
      let toneGroup = typeof ACCORDION_TONE_MAP !== 'undefined' ? ACCORDION_TONE_MAP[section.id] : null;
      if (section.id === 'clothing_group') {
        toneGroup = currentSubId;
      }
      // For face_group, only show the eyes tone picker when the eyes sub-tab is active
      if (section.id === 'face_group') {
        toneGroup = currentSubId === 'eyes' ? 'eyes' : null;
      }
      const tonePicker = toneGroup ? this.renderInlineTonePicker(toneGroup) : '';

      return `
        <div class="sub-tabs">${subTabs}</div>
        <div class="asset-grid" data-grid-for="${currentSubId}">${grid}</div>
        ${tonePicker}
      `;
    }

    return '';
  }

  renderPoseGrid() {
    // Map pose IDs to appropriate emoji icons
    const poseIcons = {
      'stand': '🧍',
      'sit': '🪑',
      'female_gaming': '🎮',
      'male_gaming': '🎮',
      'male_pose_1': '🧍‍♂️',
      'male_pose_2': '💪',
      'male_pose_3': '🕺'
    };

    // Filter out tilted variants — they're sub-poses, not separate grid items
    let visiblePoses = this.config.poses.filter(p => !p.tiltedOf);
    if (this.allowedPoses && this.allowedPoses.length > 0) {
      visiblePoses = visiblePoses.filter(p => {
        if (this.allowedPoses.includes(p.id)) return true;
        const tiltedVariant = this.config.poses.find(tp => tp.tiltedOf === p.id);
        if (tiltedVariant && this.allowedPoses.includes(tiltedVariant.id)) return true;
        return false;
      });
    }

    // Check if the current pose has a tilted variant
    const currentBasePose = this.config.poses.find(p => p.id === this.state.pose);
    const basePoseId = currentBasePose?.tiltedOf || this.state.pose;
    const tiltedPose = this.config.poses.find(p => p.tiltedOf === basePoseId);
    let hasTilted = !!tiltedPose;
    if (hasTilted && this.allowedPoses && this.allowedPoses.length > 0) {
      const baseAllowed = this.allowedPoses.includes(basePoseId);
      const tiltedAllowed = this.allowedPoses.includes(tiltedPose.id);
      hasTilted = baseAllowed && tiltedAllowed;
    }
    const isTilted = !!currentBasePose?.tiltedOf;

    let html = `<div class="asset-grid">${visiblePoses.map(pose => `
        <div class="asset-item ${(this.state.pose === pose.id || basePoseId === pose.id) ? 'asset-item--selected' : ''}"
             data-pose="${pose.id}">
          <div class="asset-item__preview">
            ${pose.icon
        ? `<img src="${resolveAssetUrl(pose.icon)}" alt="${pose.name}" loading="eager" fetchpriority="high">`
        : `<span style="font-size:24px;">${poseIcons[pose.id] || '🧍'}</span>`
      }
          </div>
          <span class="asset-item__name">${pose.name}</span>
        </div>
      `).join('')
      }</div>`;

    // Add Normal/Tilted toggle if the active pose supports it
    if (hasTilted) {
      const tiltedPoseId = this.config.poses.find(p => p.tiltedOf === basePoseId)?.id;
      html += `
        <div class="sub-pose-toggle" style="display:flex;gap:6px;margin-top:8px;">
          <button class="sub-pose-btn ${!isTilted ? 'sub-pose-btn--active' : ''}"
                  data-pose="${basePoseId}">Normal</button>
          <button class="sub-pose-btn ${isTilted ? 'sub-pose-btn--active' : ''}"
                  data-pose="${tiltedPoseId}">Tilted</button>
        </div>`;
    }

    return html;
  }

  renderAssetGrid(category) {
    const assets = this.config.assets[this.state.pose]?.[category] || [];
    const isMulti = this.isMultiSelectCategory(category);
    return assets.map(asset => {
      let isSelected;
      if (isMulti) {
        const selected = this.state.multiSelections[category] || [];
        // 'None' option is selected when array is empty
        isSelected = !asset.src ? selected.length === 0 : selected.includes(asset.id);
      } else {
        isSelected = this.state.selections[category] === asset.id;
      }
      return `
      <div class="asset-item ${isSelected ? 'asset-item--selected' : ''}"
           data-category="${category}" data-asset="${asset.id}">
        <div class="asset-item__preview">
          ${asset.src
          ? `<img src="${resolveAssetUrl(asset.src)}" alt="${asset.name}" loading="lazy" onerror="this.style.display='none'">`
          : '<span class="asset-item__none">✕</span>'}
        </div>
        <span class="asset-item__name">${asset.name}</span>
      </div>
    `;
    }).join('');
  }

  /**
   * Render the outfit set selector grid.
   */
  renderOutfitSetGrid() {
    const bundle = typeof OUTFIT_SET_BUNDLES !== 'undefined' ? OUTFIT_SET_BUNDLES[this.state.pose] : null;
    if (!bundle) return '<div style="padding:12px;color:var(--dm-text-muted);font-size:0.8rem;">No outfit sets for this pose</div>';

    const activeSet = this.state.activeOutfitSet;

    // "None" option
    let html = `
      <div class="asset-item ${!activeSet ? 'asset-item--selected' : ''}"
           data-outfit-set="none">
        <div class="asset-item__preview">
          <span class="asset-item__none">✕</span>
        </div>
        <span class="asset-item__name">None</span>
      </div>
    `;

    // Set options
    for (const set of bundle.sets) {
      const isSelected = activeSet === set.id;
      html += `
        <div class="asset-item ${isSelected ? 'asset-item--selected' : ''}"
             data-outfit-set="${set.id}">
          <div class="asset-item__preview">
            ${set.thumbnail
          ? `<img src="${resolveAssetUrl(set.thumbnail)}" alt="${set.name}" loading="lazy" onerror="this.style.display='none'">`
          : `<span style="font-size:20px;">👔</span>`}
          </div>
          <span class="asset-item__name">${set.name}</span>
        </div>
      `;
    }

    return html;
  }

  /**
   * Render an inline tone picker for a specific group (skin/hair/clothing).
   */
  renderInlineTonePicker(group) {
    const currentTone = this.state.toneSelections[group] || 'none';
    let label = group.replace('_', ' ');
    label = label.charAt(0).toUpperCase() + label.slice(1);
    const presetsList = group === 'skin' && typeof SKIN_GRADIENT_PRESETS !== 'undefined' ? SKIN_GRADIENT_PRESETS : GRADIENT_PRESETS;
    return `
      <div class="inline-tone-picker" style="margin-top:10px;padding-top:10px;border-top:1px solid var(--dm-border);">
        <div style="font-size:0.7rem;font-weight:600;color:var(--dm-text-muted);margin-bottom:6px;text-transform:uppercase;letter-spacing:0.5px;">
          🎨 ${label} Tone
        </div>
        <div class="tone-grid tone-grid--inline">
          ${presetsList.map(t => {
      const isActive = t.id === currentTone ? ' tone-swatch--active' : '';
      const bg = t.shadow
        ? `linear-gradient(135deg, ${t.shadow}, ${t.darkMid || t.shadow}, ${t.mid}, ${t.lightMid || t.highlight}, ${t.highlight})`
        : 'conic-gradient(from 0deg, #f44, #f90, #ff0, #3c0, #09f, #c3f, #f44)';
      return `<button class="tone-swatch tone-swatch--sm${isActive}" data-tone-group="${group}" data-tone-id="${t.id}" style="background:${bg}" title="${t.name}"><span class="tone-swatch__label">${t.name}</span></button>`;
    }).join('')}
        </div>
      </div>
    `;
  }

  bindControlEvents() {
    if (this._eventsBound) return;
    this._eventsBound = true;

    this.controlsPanel.addEventListener('click', e => {
      // 1. Accordion Toggle Click
      const toggleBtn = e.target.closest('[data-accordion-toggle]');
      if (toggleBtn) {
        const id = toggleBtn.dataset.accordionToggle;
        this.toggleAccordion(id);
        return;
      }

      // 2. Sub-tab Click
      const subTabBtn = e.target.closest('.sub-tab');
      if (subTabBtn) {
        const group = subTabBtn.dataset.group;
        const sub = subTabBtn.dataset.sub;
        this.activeSubSection[group] = sub;
        
        const bodyEl = this.controlsPanel.querySelector(`#acc-body-${group}`);
        const section = this.getAccordionSections().find(s => s.id === group);
        if (bodyEl && section) {
          bodyEl.innerHTML = this.renderAccordionBody(section);
        }
        return;
      }

      // 3. Tone Swatch Click
      const toneSwatch = e.target.closest('[data-tone-group]');
      if (toneSwatch) {
        const group = toneSwatch.dataset.toneGroup;
        const toneId = toneSwatch.dataset.toneId;
        this.setGroupTone(group, toneId);
        return;
      }

      // 4. Pose Click
      const poseItem = e.target.closest('[data-pose]');
      if (poseItem) {
        const poseId = poseItem.dataset.pose;
        this.setPose(poseId);
        return;
      }

      // 5. Outfit Set Click
      const outfitSetItem = e.target.closest('[data-outfit-set]');
      if (outfitSetItem) {
        const setId = outfitSetItem.dataset.outfitSet;
        this.selectOutfitSet(setId === 'none' ? null : setId);
        return;
      }

      // 6. Asset Click
      const assetItem = e.target.closest('[data-category]');
      if (assetItem) {
        const category = assetItem.dataset.category;
        const assetId = assetItem.dataset.asset;
        this.selectAsset(category, assetId);
        return;
      }
    });
  }

  toggleAccordion(id) {
    const wasOpen = this.openAccordion === id;
    this.openAccordion = wasOpen ? null : id;

    this.controlsPanel.querySelectorAll('.accordion').forEach(acc => {
      const accId = acc.dataset.accordion;
      if (accId === id && !wasOpen) {
        acc.classList.add('accordion--open');
      } else {
        acc.classList.remove('accordion--open');
      }
    });

    // Lazy preload opened accordion categories
    if (!wasOpen && id && id !== 'pose') {
      this.preloadAccordionCategories(id);
    }
  }

  preloadAccordionCategories(accordionId) {
    const section = this.getAccordionSections().find(s => s.id === accordionId);
    if (!section) return;

    const categories = section.subSections
      ? section.subSections.map(sub => sub.id)
      : [accordionId];

    const urls = [];
    const activePoseId = this.state.pose;
    const poseAssets = this.config.assets[activePoseId];
    if (!poseAssets) return;

    for (const cat of categories) {
      const assets = poseAssets[cat] || [];
      for (const asset of assets) {
        if (asset.src) {
          urls.push(asset.src);
        }
      }
    }

    const concurrency = 4;
    (async () => {
      for (let i = 0; i < urls.length; i += concurrency) {
        // Abort if the user switched pose or closed/changed the accordion
        if (this.state.pose !== activePoseId || this.openAccordion !== accordionId) {
          break;
        }
        const chunk = urls.slice(i, i + concurrency);
        await Promise.allSettled(chunk.map(src => this.loadImage(src)));
        await new Promise(resolve => setTimeout(resolve, 16));
      }
    })();
  }

  // ==================== STATE ====================

  selectAsset(category, assetId) {
    // #3: Multi-select for extras (accessories)
    if (this.isMultiSelectCategory(category)) {
      this.toggleMultiSelect(category, assetId);
      this.loadAsset(category);
      return;
    }

    this.state.selections[category] = assetId;

    // #2: Hair style sync
    const isHairCategory = category === 'hair_front' || category === 'hair_side' || category === 'hair_back';
    if (isHairCategory) {
      this.applyHairStyleSync(category, assetId);
    }

    // If the user manually changes a piece of a bundled outfit, deactivate the 'activeOutfitSet' highlight
    // so they are now in a "custom" mixed state.
    const bundle = typeof OUTFIT_SET_BUNDLES !== 'undefined' ? OUTFIT_SET_BUNDLES[this.state.pose] : null;
    let didDeactivateOutfitSet = false;
    if (bundle && bundle.bundledCategories.includes(category)) {
      if (this.state.activeOutfitSet) {
        this.state.activeOutfitSet = null;
        didDeactivateOutfitSet = true;
      }
    }

    // Update DOM classes for selected asset directly instead of full renderControls
    const gridContainer = this.controlsPanel.querySelector(`[data-grid-for="${category}"]`);
    console.log("[Character] selectAsset DOM update:", { category, gridContainerExists: !!gridContainer });
    if (gridContainer) {
      gridContainer.querySelectorAll('.asset-item').forEach(item => {
        item.classList.toggle('asset-item--selected', item.dataset.asset === assetId);
      });
    }

    // If an outfit set was deactivated, update the outfit set grid highlight to "None" in DOM
    if (didDeactivateOutfitSet) {
      const outfitGridContainer = this.controlsPanel.querySelector(`[data-grid-for="outfit_set"]`);
      console.log("[Character] outfit set deactivated DOM update:", { outfitGridContainerExists: !!outfitGridContainer });
      if (outfitGridContainer) {
        outfitGridContainer.querySelectorAll('.asset-item').forEach(item => {
          item.classList.toggle('asset-item--selected', item.dataset.outfitSet === 'none');
        });
      }
    }

    this.loadAsset(category);

    // #2: If hair synced, reload synced targets
    if (isHairCategory) {
      const syncConfig = typeof HAIR_FRONT_SYNC_TARGETS !== 'undefined' ? HAIR_FRONT_SYNC_TARGETS[this.state.pose] : null;
      if (syncConfig) {
        const syncCategories = ['hair_front', ...syncConfig.targets];
        if (syncCategories.includes(category)) {
          for (const targetCat of syncCategories) {
            if (targetCat !== category) this.loadAsset(targetCat);
          }
        }
      } else if (category === 'hair_front') {
        this.loadAsset('hair_back');
      }
    }
  }

  // #3: Check if a category supports multi-select
  isMultiSelectCategory(category) {
    return category === 'extras';
  }

  // #3: Toggle an asset in/out of the multi-select array
  toggleMultiSelect(category, assetId) {
    if (!this.state.multiSelections[category]) {
      this.state.multiSelections[category] = [];
    }
    const arr = this.state.multiSelections[category];

    // 'None' selection clears all
    const assets = this.config.assets[this.state.pose]?.[category] || [];
    const asset = assets.find(a => a.id === assetId);
    if (asset && !asset.src) {
      // This is a "None" option — clear all
      this.state.multiSelections[category] = [];
    } else {
      const idx = arr.indexOf(assetId);
      if (idx >= 0) {
        arr.splice(idx, 1); // deselect
      } else {
        arr.push(assetId); // add
      }
    }

    // Update DOM classes for multi-select directly
    const gridContainer = this.controlsPanel.querySelector(`[data-grid-for="${category}"]`);
    if (gridContainer) {
      const selected = this.state.multiSelections[category];
      gridContainer.querySelectorAll('.asset-item').forEach(item => {
        const itemAssetId = item.dataset.asset;
        const itemAsset = assets.find(a => a.id === itemAssetId);
        let isSelected;
        if (!itemAsset || !itemAsset.src) {
          isSelected = selected.length === 0;
        } else {
          isSelected = selected.includes(itemAssetId);
        }
        item.classList.toggle('asset-item--selected', isSelected);
      });
    }
  }

  // #2: Hair style sync — when any hair part changes, auto-sync the others
  applyHairStyleSync(sourceCategory, sourceAssetId) {
    // --- Legacy sync for standing/sitting poses (HAIR_STYLE_SYNC) ---
    if (sourceCategory === 'hair_front' && typeof HAIR_STYLE_SYNC !== 'undefined') {
      const group = HAIR_STYLE_SYNC.find(g => g.front === sourceAssetId);
      if (group) {
        const backAssets = this.config.assets[this.state.pose]?.hair_back || [];
        if (backAssets.find(a => a.id === group.back)) {
          this.state.selections.hair_back = group.back;
        }
        return;
      }
      // 'None' front → 'None' back (legacy IDs)
      if (sourceAssetId === 'hf0') {
        this.state.selections.hair_back = 'hb0';
        return;
      }
    }

    // --- New poses: style-number-based sync (HAIR_FRONT_SYNC_TARGETS) ---
    if (typeof HAIR_FRONT_SYNC_TARGETS === 'undefined') return;
    const syncConfig = HAIR_FRONT_SYNC_TARGETS[this.state.pose];
    if (!syncConfig) return;

    const syncCategories = ['hair_front', ...syncConfig.targets];
    if (!syncCategories.includes(sourceCategory)) return;

    const poseAssets = this.config.assets[this.state.pose] || {};

    // Extract style number from source asset ID (e.g. _s1, _s2, _s5, _1, _2) or set number (e.g. _set1_)
    const styleMatch = sourceAssetId.match(/_s(\d+)$/) || sourceAssetId.match(/_(\d+)$/);
    const setMatch = sourceAssetId.match(/_set(\d+)_/);

    for (const targetCat of syncCategories) {
      if (targetCat === sourceCategory) continue;

      const targetAssets = poseAssets[targetCat] || [];
      if (styleMatch) {
        const styleNum = styleMatch[1];
        // Find matching style in target category
        const match = targetAssets.find(a => a.id && (a.id.endsWith(`_s${styleNum}`) || a.id.endsWith(`_${styleNum}`)));
        if (match) {
          this.state.selections[targetCat] = match.id;
        } else {
          // No matching style — set to 'None'
          const noneAsset = targetAssets.find(a => !a.src);
          if (noneAsset) {
            this.state.selections[targetCat] = noneAsset.id;
          }
        }
      } else if (setMatch) {
        const setNum = setMatch[1];
        // Find matching set hair in target category
        const match = targetAssets.find(a => a.id && a.id.includes(`_set${setNum}_`));
        if (match) {
          this.state.selections[targetCat] = match.id;
        } else {
          const noneAsset = targetAssets.find(a => !a.src);
          if (noneAsset) {
            this.state.selections[targetCat] = noneAsset.id;
          }
        }
      } else {
        // Source hair has no style number — set target to 'None'
        const noneAsset = targetAssets.find(a => !a.src);
        if (noneAsset) {
          this.state.selections[targetCat] = noneAsset.id;
        }
      }
    }
  }

  /**
   * Outfit Set bundling — select a complete outfit set that replaces
   * multiple normal component layers at once.
   */
  selectOutfitSet(setId) {
    const bundle = typeof OUTFIT_SET_BUNDLES !== 'undefined' ? OUTFIT_SET_BUNDLES[this.state.pose] : null;
    if (!bundle) return;

    // Store active set in state
    this.state.activeOutfitSet = setId || null;

    if (!setId || setId === 'none') {
      // Clear set — revert bundled categories to first available asset
      this.state.activeOutfitSet = null;
      const poseAssets = this.config.assets[this.state.pose] || {};
      for (const cat of bundle.bundledCategories) {
        const catAssets = poseAssets[cat] || [];
        // Pick the first asset (which is a real one since None is pushed to end)
        if (catAssets.length > 0) {
          this.state.selections[cat] = catAssets[0].id;
        }
      }
    } else {
      // Find the set definition
      const setDef = bundle.sets.find(s => s.id === setId);
      if (!setDef) return;
      this.applyOutfitSetSync(setDef.setNum);
    }

    // Reload all bundled categories
    for (const cat of bundle.bundledCategories) {
      this.loadAsset(cat);
    }

    // Update Outfit Set grid highlights directly in the DOM
    const outfitGridContainer = this.controlsPanel.querySelector(`[data-grid-for="outfit_set"]`);
    console.log("[Character] selectOutfitSet DOM update:", { setId, outfitGridContainerExists: !!outfitGridContainer });
    if (outfitGridContainer) {
      outfitGridContainer.querySelectorAll('.asset-item').forEach(item => {
        const itemSetId = item.dataset.outfitSet;
        const isSelected = (setId || 'none') === itemSetId;
        item.classList.toggle('asset-item--selected', isSelected);
      });
    }
  }

  /**
   * Auto-sync all bundled categories to match the given set number.
   * Finds the first asset in each category whose ID contains `_set<N>_`.
   * If no match found, defaults to the 'None' option (clears that layer).
   */
  applyOutfitSetSync(setNum, excludeCategory = null) {
    const bundle = typeof OUTFIT_SET_BUNDLES !== 'undefined' ? OUTFIT_SET_BUNDLES[this.state.pose] : null;
    if (!bundle) return;

    const poseAssets = this.config.assets[this.state.pose] || {};

    for (const cat of bundle.bundledCategories) {
      if (cat === excludeCategory) continue; // Keep the user's specific selection for this category

      const catAssets = poseAssets[cat] || [];
      const matchingAsset = catAssets.find(a => a.id && a.id.includes(`_set${setNum}_`));
      if (matchingAsset) {
        this.state.selections[cat] = matchingAsset.id;
      } else {
        // No set variant for this category — set to 'None' to clear the layer
        const noneAsset = catAssets.find(a => !a.src);
        if (noneAsset) {
          this.state.selections[cat] = noneAsset.id;
        }
      }
    }
  }

  setPose(poseId) {
    if (this.state.pose === poseId) return;

    const oldPoseId = this.state.pose;
    this.state.pose = poseId;

    const newPoseAssets = this.config.assets[poseId] || {};

    const criticalCategories = [
      'top_clothing', 'bottom_clothing', 'outfit_upper', 'outfit_bottom', 'outfit_outer', 'outfit_full', 'shoes',
      'hair_front', 'hair_back', 'hair_side',
      'eyes', 'brows', 'mouth', 'nose', 'expression'
    ];

    // Preserve selections by ID match or index fallback for categories
    // that exist in both old and new poses
    Object.keys(this.state.selections).forEach(key => {
      if (key === 'pose') return;
      const newAssets = newPoseAssets[key];
      if (!newAssets || newAssets.length === 0) return;

      const exactMatch = newAssets.find(a => a.id === this.state.selections[key]);
      if (exactMatch) return;

      const oldAssets = this.config.assets[oldPoseId]?.[key] || [];
      const oldAsset = oldAssets.find(a => a.id === this.state.selections[key]);

      // 1. If the old selection was a "None" option (src is null/falsy), try to map it to the "None" option in the new assets
      if (oldAsset && !oldAsset.src) {
        const newNone = newAssets.find(a => !a.src);
        if (newNone) {
          this.state.selections[key] = newNone.id;
          return;
        }
      }

      // 2. Otherwise, fall back to index matching
      const oldIndex = oldAssets.findIndex(a => a.id === this.state.selections[key]);
      if (oldIndex >= 0) {
        // If the mapped index points to a "None" option (src is null/falsy) in the new assets,
        // but the old selection was a real asset (had a valid src), try to find a valid real asset.
        const mappedAsset = newAssets[oldIndex];
        if (mappedAsset && !mappedAsset.src && oldAsset && oldAsset.src) {
          const firstRealAsset = newAssets.find(a => a.src);
          if (firstRealAsset) {
            this.state.selections[key] = firstRealAsset.id;
            return;
          }
        }
        if (oldIndex < newAssets.length) {
          this.state.selections[key] = newAssets[oldIndex].id;
          return;
        }
      }

      // Fall back if index is out of bounds or -1
      // For critical/visible categories, try to choose a non-None asset if the old selection was a real asset
      if (criticalCategories.includes(key) && oldAsset && oldAsset.src) {
        const firstRealAsset = newAssets.find(a => a.src);
        if (firstRealAsset) {
          this.state.selections[key] = firstRealAsset.id;
          return;
        }
      }
      this.state.selections[key] = newAssets[0].id;
    });

    // Initialize default selections for categories that are NEW to this pose
    // (categories the old pose didn't have, so they weren't in state.selections)
    for (const [category, assets] of Object.entries(newPoseAssets)) {
      if (!this.state.selections[category] && assets.length > 0) {
        let defaultAsset = null;
        if (criticalCategories.includes(category)) {
          // For critical categories, try to default to the first active/non-None asset to avoid bald/naked/blank characters
          defaultAsset = assets.find(a => a.src);
        }
        if (!defaultAsset) {
          // Fall back to None option if it exists
          defaultAsset = assets.find(a => !a.src) || assets[0];
        }
        this.state.selections[category] = defaultAsset.id;
      }
    }

    // Auto-sync hair styles if hair_front is available
    if (this.state.selections.hair_front) {
      this.applyHairStyleSync('hair_front', this.state.selections.hair_front);
    }

    // Auto-sync outfit set if one is active, or initialize default set if switching to a pose with bundles
    const bundle = typeof OUTFIT_SET_BUNDLES !== 'undefined' ? OUTFIT_SET_BUNDLES[poseId] : null;
    if (bundle && bundle.sets.length > 0) {
      if (!this.state.activeOutfitSet) {
        // Default to the first set in the bundle (e.g. Set 1)
        this.state.activeOutfitSet = bundle.sets[0].id;
      }
      let setDef = bundle.sets.find(s => s.id === this.state.activeOutfitSet);
      if (!setDef) {
        // Fallback to first set if previous set ID is not found in the new bundle
        this.state.activeOutfitSet = bundle.sets[0].id;
        setDef = bundle.sets[0];
      }
      this.applyOutfitSetSync(setDef.setNum);
    } else {
      this.state.activeOutfitSet = null;
    }

    // Resize stage if the new pose has different canvas dimensions
    const { width, height } = this.getCanvasSize();
    this.stage.width(width);
    this.stage.height(height);

    this.recreateLayers();
    this.handleResize();
    this.renderControls();
    this.loadCharacter();

    // Restart preload with new pose as Tier 1 priority
    if (!this.skipPreload) {
      this.preloadAllAssets();
    }
  }

  randomize() {
    const poseId = this.state.pose;
    const assets = this.config.assets[poseId];
    if (!assets) return;

    // Track which categories have already been set by sync logic
    const syncedCategories = new Set();

    // ---- 1. Outfit Set: 50% chance to pick a set (if bundles exist) ----
    const bundle = typeof OUTFIT_SET_BUNDLES !== 'undefined' ? OUTFIT_SET_BUNDLES[poseId] : null;
    if (bundle && bundle.sets.length > 0 && Math.random() < 0.5) {
      const randomSet = bundle.sets[Math.floor(Math.random() * bundle.sets.length)];
      this.state.activeOutfitSet = randomSet.id;
      this.applyOutfitSetSync(randomSet.setNum);
      // Mark all bundled categories as already handled
      bundle.bundledCategories.forEach(cat => syncedCategories.add(cat));
    } else {
      this.state.activeOutfitSet = null;
    }

    // ---- 2. Hair sync: pick hair_front first, then auto-sync targets ----
    const hairFrontAssets = assets.hair_front;
    if (hairFrontAssets && hairFrontAssets.length > 0 && !syncedCategories.has('hair_front')) {
      // Pick a random hair_front (prefer non-None for visual variety)
      const nonNone = hairFrontAssets.filter(a => a.src);
      const pool = nonNone.length > 0 ? nonNone : hairFrontAssets;
      const randomFront = pool[Math.floor(Math.random() * pool.length)];
      this.state.selections.hair_front = randomFront.id;
      syncedCategories.add('hair_front');

      // Auto-sync hair targets
      this.applyHairStyleSync('hair_front', randomFront.id);

      // Mark all hair sync targets as handled
      if (typeof HAIR_FRONT_SYNC_TARGETS !== 'undefined') {
        const syncConfig = HAIR_FRONT_SYNC_TARGETS[poseId];
        if (syncConfig) {
          syncConfig.targets.forEach(cat => syncedCategories.add(cat));
        }
      }
      // Legacy sync always targets hair_back
      if (typeof HAIR_STYLE_SYNC !== 'undefined') {
        syncedCategories.add('hair_back');
      }
    }

    // ---- 3. Remaining categories: pick random assets (skip synced) ----
    Object.keys(assets).forEach(category => {
      if (syncedCategories.has(category)) return;

      const items = assets[category];
      if (!items || items.length === 0) return;

      // Multi-select categories (extras): randomly toggle 0-2 items
      if (category === 'extras') {
        const withSrc = items.filter(a => a.src);
        if (withSrc.length === 0) {
          this.state.multiSelections[category] = [];
        } else {
          const count = Math.floor(Math.random() * Math.min(3, withSrc.length + 1)); // 0, 1, or 2
          const shuffled = withSrc.sort(() => Math.random() - 0.5);
          this.state.multiSelections[category] = shuffled.slice(0, count).map(a => a.id);
        }
        return;
      }

      // Single-select: pick a random asset (prefer non-None for variety)
      const nonNone = items.filter(a => a.src);
      const pool = nonNone.length > 0 ? nonNone : items;
      this.state.selections[category] = pool[Math.floor(Math.random() * pool.length)].id;
    });

    // ---- 4. Randomize tones ----
    if (this.state.toneSelections) {
      for (const group of Object.keys(this.state.toneSelections)) {
        const presetsList = group === 'skin' && typeof SKIN_GRADIENT_PRESETS !== 'undefined'
          ? SKIN_GRADIENT_PRESETS
          : GRADIENT_PRESETS;
        this.state.toneSelections[group] = presetsList[Math.floor(Math.random() * presetsList.length)].id;
      }
    }

    this.renderControls();
    this.loadCharacter();
  }

  reset() {
    const initialPose = this.getDefaultPose();
    this.state.pose = initialPose;
    this.state.selections = { ...this.config.defaults };
    // If initialPose is different from defaults.pose, initialize its categories
    if (initialPose !== this.config.defaults.pose) {
      const poseAssets = this.config.assets[initialPose] || {};
      for (const [category, assets] of Object.entries(poseAssets)) {
        if (!this.state.selections[category] && assets.length > 0) {
          this.state.selections[category] = assets[0].id;
        }
      }
    }
    // #3: Reset multi-selections
    this.state.multiSelections = {
      extras: this.config.defaults.extras ? [this.config.defaults.extras] : []
    };
    this.state.toneSelections = { skin: 'none', hair: 'none', top_clothing: 'none', bottom_clothing: 'none', shoes: 'none' };
    this.state.activeOutfitSet = null;
    this.recreateLayers();
    this.renderControls();
    this.loadCharacter();
  }

  // ==================== CANVAS ====================

  async loadCharacter() {
    const pose = this.config.poses.find(p => p.id === this.state.pose);
    if (!pose) return;

    await Promise.all(pose.zIndexConfig.map(cat => this.loadAsset(cat)));
  }

  async loadAsset(category) {
    const group = this.layerGroups[category];
    if (!group) return;

    this.loadCounters[category] = (this.loadCounters[category] || 0) + 1;
    const loadId = this.loadCounters[category];

    this.deallocateCanvases(group);
    group.destroyChildren();

    try {
      // #3: Multi-select categories load multiple assets
      if (this.isMultiSelectCategory(category)) {
        const selectedIds = this.state.multiSelections[category] || [];
        if (selectedIds.length === 0) {
          return;
        }

        const assets = this.config.assets[this.state.pose]?.[category] || [];
        const tone = this.getActiveToneForCategory(category);

        for (const assetId of selectedIds) {
          const asset = assets.find(a => a.id === assetId);
          if (!asset || !asset.src) continue;

          let srcPath = asset.src;
          if (tone) {
            const grayPath = resolveGrayAssetPath(asset.src);
            if (grayPath) srcPath = grayPath;
          }

          const image = await this.loadImage(srcPath);
          if (this.loadCounters[category] !== loadId) return;

          let imageSource = image;
          if (tone) {
            imageSource = GradientMap.apply(image, tone);
          }

          const cSize = this.getCanvasSize();
          const konvaImage = new Konva.Image({
            x: 0, y: 0,
            image: imageSource,
            width: cSize.width,
            height: cSize.height
          });

          group.add(konvaImage);
        }
        return;
      }

      // Standard single-select path
      const assetId = this.state.selections[category];
      const assets = this.config.assets[this.state.pose]?.[category];
      const asset = assets?.find(a => a.id === assetId);

      if (!asset || !asset.src) {
        return;
      }

      // Determine if gradient mapping is active for this category
      const tone = this.getActiveToneForCategory(category);
      let srcPath = asset.src;
      if (tone) {
        const grayPath = resolveGrayAssetPath(asset.src);
        if (grayPath) srcPath = grayPath;
      }

      const image = await this.loadImage(srcPath);
      if (this.loadCounters[category] !== loadId) return;

      // Apply gradient map if tone is active
      let imageSource = image;
      if (tone) {
        imageSource = GradientMap.apply(image, tone);
      }

      const cSize = this.getCanvasSize();
      const konvaImage = new Konva.Image({
        x: 0, y: 0,
        image: imageSource,
        width: cSize.width,
        height: cSize.height
      });

      group.add(konvaImage);

    } catch (err) {
      const assetId = this.state.selections[category];
      const assets = this.config.assets[this.state.pose]?.[category];
      const asset = assets?.find(a => a.id === assetId);
      console.warn(`[Character] Failed to load: ${asset ? asset.src : category}`, err);
    } finally {
      if (this.loadCounters[category] === loadId) {
        this.mainLayer.batchDraw();
      }
    }
  }

  /**
   * Get the active gradient tone for a specific category, or null if none.
   * Looks up the category's group (skin/hair/clothing) and returns the preset.
   */
  getActiveToneForCategory(category) {
    const group = GRADIENT_TONE_GROUPS[category];
    if (!group) return null;
    let toneId = this.state.toneSelections[group];

    // Fallback to outfit_set tone if this category is part of an active outfit bundle
    // and no specific tone is set for the category itself.
    // We explicitly exclude 'skin' and 'hair' groups from this fallback so clothing tones don't bleed into them.
    if ((!toneId || toneId === 'none') && group !== 'skin' && group !== 'hair' && group !== 'eyes' && typeof OUTFIT_SET_BUNDLES !== 'undefined') {
      const bundle = OUTFIT_SET_BUNDLES[this.state.pose];
      if (bundle && bundle.bundledCategories.includes(category)) {
        const outfitSetTone = this.state.toneSelections['outfit_set'];
        if (outfitSetTone && outfitSetTone !== 'none') {
          toneId = outfitSetTone;
        }
      }
    }

    if (!toneId || toneId === 'none') return null;
    const presetsList = group === 'skin' && typeof SKIN_GRADIENT_PRESETS !== 'undefined' ? SKIN_GRADIENT_PRESETS : GRADIENT_PRESETS;
    const preset = presetsList.find(p => p.id === toneId);
    return preset && preset.shadow ? preset : null;
  }

  setGroupTone(group, toneId) {
    this.state.toneSelections[group] = toneId;

    if (group === 'outfit_set' && typeof OUTFIT_SET_BUNDLES !== 'undefined') {
      // Reload all categories in the active pose's outfit bundle
      const bundle = OUTFIT_SET_BUNDLES[this.state.pose];
      if (bundle) {
        for (const cat of bundle.bundledCategories) {
          // Clear any custom tones on the individual components so they inherit the new set tone
          const catGroup = GRADIENT_TONE_GROUPS[cat];
          if (catGroup && catGroup !== 'skin' && catGroup !== 'hair') {
            this.state.toneSelections[catGroup] = null;
          }
          this.loadAsset(cat);
        }
      }
    } else {
      // Reload only categories belonging to this group
      for (const [cat, g] of Object.entries(GRADIENT_TONE_GROUPS)) {
        if (g === group) this.loadAsset(cat);
      }
    }
    
    // Update tone swatch highlights directly in the DOM
    const swatches = this.controlsPanel.querySelectorAll(`[data-tone-group="${group}"]`);
    swatches.forEach(swatch => {
      const isSelected = (toneId || 'none') === swatch.dataset.toneId;
      swatch.classList.toggle('tone-swatch--active', isSelected);
    });
  }

  loadImage(src) {
    // Request downsized images from Shopify CDN (2× canvas width for Retina).
    // Admin pages get full-resolution (resolveAssetUrl skips maxWidth on admin).
    const { width } = this.getCanvasSize();
    const maxWidth = width * 2;
    const resolved = resolveAssetUrl(src, { maxWidth });
    return new Promise((resolve, reject) => {
      if (this.imageCache.has(resolved)) {
        resolve(this.imageCache.get(resolved));
        return;
      }
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        // LRU Cache eviction check
        if (this.imageCache.size >= this.maxCacheSize) {
          const oldestKey = this.imageCache.keys().next().value;
          this.imageCache.delete(oldestKey);
        }
        this.imageCache.set(resolved, img);
        resolve(img);
      };
      img.onerror = reject;
      img.src = resolved;
    });
  }
  // ==================== RECIPE ====================

  generateRecipe() {
    return {
      pose: this.state.pose,
      selections: { ...this.state.selections },
      // #3: Include multi-select data
      multiSelections: JSON.parse(JSON.stringify(this.state.multiSelections)),
      toneSelections: { ...this.state.toneSelections }
    };
  }

  /**
   * Export the current character as a data URL (for scene composer)
   */
  toDataURL(options = {}) {
    return this.stage.toDataURL({ pixelRatio: options.pixelRatio || 1 });
  }
}
